<script setup lang="ts">
import { ref, watch } from 'vue'

interface Props {
  helperText?: string
  clickMode?: boolean
  noPopup?: boolean
  warningIcon?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{ click: [] }>()

const showTooltip = ref(false)
const containerRef = ref<HTMLElement | null>(null)
const tooltipStyle = ref({ top: '0px', left: '0px' })

const calcPosition = () => {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  tooltipStyle.value = {
    top: `${rect.bottom + window.scrollY + 8}px`,
    left: `${rect.left + window.scrollX}px`,
  }
}

const handleMouseEnter = () => {
  if (!props.clickMode) {
    calcPosition()
    showTooltip.value = true
  }
}
const handleMouseLeave = () => { if (!props.clickMode) showTooltip.value = false }
const handleClick = () => {
  if (props.clickMode) {
    emit('click')
    if (!props.noPopup) {
      if (!showTooltip.value) calcPosition()
      showTooltip.value = !showTooltip.value
    }
  }
}

const onClickOutside = (e: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
    showTooltip.value = false
  }
}

watch(showTooltip, (val) => {
  if (props.clickMode) {
    if (val) document.addEventListener('click', onClickOutside)
    else document.removeEventListener('click', onClickOutside)
  }
})
</script>

<template>
  <div ref="containerRef" class="relative flex">
    <!-- Warning Icon Button -->
    <button
      v-if="warningIcon"
      type="button"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
      :class="['inline-flex items-center justify-center w-4 h-4', clickMode ? 'cursor-pointer' : 'cursor-help']"
    >
      <svg viewBox="0 0 24 24" fill="none" class="w-full h-full">
        <path d="M12 2L1 21h22L12 2z" fill="#FFC107" stroke="#F59E0B" stroke-width="1" stroke-linejoin="round"/>
        <rect x="11" y="9" width="2" height="5.5" rx="1" fill="#1a1a1a"/>
        <circle cx="12" cy="17.5" r="1.2" fill="#1a1a1a"/>
      </svg>
    </button>
    <!-- Info Icon Button -->
    <button
      v-else
      type="button"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @click="handleClick"
      :class="['inline-flex items-center justify-center w-4 h-4', clickMode ? 'cursor-pointer' : 'cursor-help']"
    >
      <svg v-if="!showTooltip" viewBox="0 0 24 24" fill="none" class="w-full h-full text-primary-600">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <line x1="12" y1="10" x2="12" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="7" r="1" fill="currentColor"/>
      </svg>
      <svg v-if="showTooltip" viewBox="0 0 24 24" fill="rgb(37, 99, 235)" class="w-full h-full">
        <circle cx="12" cy="12" r="10" stroke="rgb(37, 99, 235)" stroke-width="2"/>
        <line x1="12" y1="10" x2="12" y2="16" stroke="white" stroke-width="2" stroke-linecap="round"/>
        <circle cx="12" cy="7" r="1.2" fill="white"/>
      </svg>
    </button>

    <!-- Tooltip / Popup — rendered via Teleport to escape overflow:hidden parents -->
    <Teleport to="body">
      <transition name="fade">
        <div
          v-if="showTooltip"
          :style="{ position: 'absolute', top: tooltipStyle.top, left: tooltipStyle.left, zIndex: 9999 }"
          :class="['rounded-lg p-3 w-56 text-xs text-gray-700 shadow-lg', warningIcon ? 'bg-yellow-50 border border-yellow-300' : 'bg-primary-50 border border-primary-300']"
        >
          {{ helperText }}
          <!-- Tooltip Arrow -->
          <div :class="['absolute left-4 -top-1.5 w-3 h-3 border-l border-t rotate-45', warningIcon ? 'bg-yellow-50 border-yellow-300' : 'bg-primary-50 border-primary-300']"></div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
